import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  serverTimestamp,
  increment
} from 'firebase/firestore'
import { db } from '../config/firebase'
import type { Chart, CreateChart, UpdateChart } from '@shared/schemas/chart'
import { httpsCallable } from 'firebase/functions'
import { functions } from '../config/firebase'

export class ChartService {
  /**
   * Create a new chart for a user in an organization
   */
  static async create(chartData: CreateChart): Promise<Chart> {
    const { organizationId, userId, ...data } = chartData
    
    // Create the chart document in the hierarchical structure
    const chartsRef = collection(db, 'organizations', organizationId, 'users', userId, 'charts')
    
    const chartDoc = {
      ...data,
      organizationId,
      userId,
      isPublic: data.isPublic ?? false,
      viewCount: 0,
      configuration: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top' as const
          },
          title: {
            display: true,
            text: data.title
          }
        },
        ...data.configuration
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }

    const docRef = await addDoc(chartsRef, chartDoc)
    
    const createdDoc = await getDoc(docRef)
    return {
      id: docRef.id,
      ...createdDoc.data(),
      createdAt: createdDoc.data()?.createdAt?.toDate() || new Date(),
      updatedAt: createdDoc.data()?.updatedAt?.toDate() || new Date(),
      lastViewedAt: createdDoc.data()?.lastViewedAt?.toDate()
    } as Chart
  }

  /**
   * Get a chart by ID
   */
  static async getById(organizationId: string, userId: string, chartId: string): Promise<Chart | null> {
    const chartRef = doc(db, 'organizations', organizationId, 'users', userId, 'charts', chartId)
    const chartDoc = await getDoc(chartRef)
    
    if (!chartDoc.exists()) return null
    
    const data = chartDoc.data()
    return {
      id: chartDoc.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      lastViewedAt: data.lastViewedAt?.toDate()
    } as Chart
  }

  /**
   * Update a chart
   */
  static async update(organizationId: string, userId: string, chartId: string, updates: UpdateChart): Promise<void> {
    const chartRef = doc(db, 'organizations', organizationId, 'users', userId, 'charts', chartId)
    
    await updateDoc(chartRef, {
      ...updates,
      updatedAt: serverTimestamp()
    })
  }

  /**
   * Delete a chart
   */
  static async delete(organizationId: string, userId: string, chartId: string): Promise<void> {
    const chartRef = doc(db, 'organizations', organizationId, 'users', userId, 'charts', chartId)
    await deleteDoc(chartRef)
  }

  /**
   * Get all charts for a user in an organization
   */
  static async getUserCharts(organizationId: string, userId: string): Promise<Chart[]> {
    const chartsRef = collection(db, 'organizations', organizationId, 'users', userId, 'charts')
    const q = query(chartsRef, orderBy('updatedAt', 'desc'))
    
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map(doc => {
      const data = doc.data()
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        lastViewedAt: data.lastViewedAt?.toDate()
      } as Chart
    })
  }

  /**
   * Get all charts for an organization (across all users)
   */
  static async getOrganizationCharts(organizationId: string, limitCount?: number): Promise<Chart[]> {
    // Note: This requires a composite query across subcollections
    // For now, we'll need to query each user's charts individually
    // In production, consider using a flattened structure or Cloud Functions for aggregation
    
    // This is a simplified version - you might want to implement pagination
    // and more efficient querying based on your needs
    const charts: Chart[] = []
    
    // Get all users in the organization first
    const usersRef = collection(db, 'organizations', organizationId, 'users')
    const usersSnapshot = await getDocs(usersRef)
    
    for (const userDoc of usersSnapshot.docs) {
      const userCharts = await this.getUserCharts(organizationId, userDoc.id)
      charts.push(...userCharts)
    }
    
    // Sort by updatedAt and apply limit
    charts.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())
    
    return limitCount ? charts.slice(0, limitCount) : charts
  }

  /**
   * Get public charts for an organization
   */
  static async getPublicCharts(organizationId: string, limitCount?: number): Promise<Chart[]> {
    const allCharts = await this.getOrganizationCharts(organizationId, limitCount)
    return allCharts.filter(chart => chart.isPublic)
  }

  /**
   * Search charts by title or description
   */
  static async searchCharts(organizationId: string, userId: string, searchTerm: string): Promise<Chart[]> {
    const userCharts = await this.getUserCharts(organizationId, userId)
    
    const searchLower = searchTerm.toLowerCase()
    return userCharts.filter(chart => 
      chart.title.toLowerCase().includes(searchLower) ||
      chart.description?.toLowerCase().includes(searchLower) ||
      chart.tags?.some(tag => tag.toLowerCase().includes(searchLower))
    )
  }

  /**
   * Update chart view count and last viewed time
   */
  static async recordView(organizationId: string, userId: string, chartId: string): Promise<void> {
    const chartRef = doc(db, 'organizations', organizationId, 'users', userId, 'charts', chartId)
    
    await updateDoc(chartRef, {
      viewCount: increment(1),
      lastViewedAt: serverTimestamp()
    })
  }

  /**
   * Get charts by type
   */
  static async getChartsByType(organizationId: string, userId: string, chartType: string): Promise<Chart[]> {
    const chartsRef = collection(db, 'organizations', organizationId, 'users', userId, 'charts')
    const q = query(
      chartsRef, 
      where('type', '==', chartType),
      orderBy('updatedAt', 'desc')
    )
    
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map(doc => {
      const data = doc.data()
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        lastViewedAt: data.lastViewedAt?.toDate()
      } as Chart
    })
  }

  /**
   * Update only the chart title (for inline editing)
   */
  static async updateTitle(organizationId: string, userId: string, chartId: string, title: string): Promise<void> {
    const chartRef = doc(db, 'organizations', organizationId, 'users', userId, 'charts', chartId)
    
    await updateDoc(chartRef, {
      title,
      'configuration.plugins.title.text': title,
      updatedAt: serverTimestamp()
    })
  }

  /**
   * Generate an AI-powered chart title from user intent
   */
  static async generateAITitle(userIntent: string, chartContext?: any): Promise<string> {
    try {
      // Attempting to call the cloud function. This is expected to either
      // return a basic title from the backend or fail, triggering the catch.
      const createTitleFunc = httpsCallable(functions, 'createChartTitleFromIntent');
      const result = await createTitleFunc({ 
        userIntent, 
        chartContext 
      });
      
      const data = result.data as { success: boolean; title: string };
      
      if (data.success && data.title) {
        return data.title;
      } else {
        throw new Error('AI title generation is currently unavailable.');
      }
    } catch (error) {
      console.warn('AI title generation cloud function is unavailable. Falling back to local basic title generation.', error);
      return ChartService.generateBasicTitle(userIntent);
    }
  }

  /**
   * Fallback basic title generation (for when AI fails or is unavailable)
   */
  static generateBasicTitle(intent: string): string {
    const words = intent.toLowerCase().split(/\s+/)
    const actionWords = ['show', 'display', 'chart', 'graph', 'visualize', 'me', 'the', 'i', 'want', 'to', 'see', 'please']
    const meaningfulWords = words.filter(word => 
      !actionWords.includes(word) && 
      word.length > 2 && 
      !word.match(/^(for|from|with|and|but|or)$/i)
    )
    
    if (meaningfulWords.length > 0) {
      const titleWords = meaningfulWords.slice(0, 5)
      return titleWords.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
    }
    
    return 'Data Visualization'
  }
} 